import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import { ArrowLeft, Flame } from "lucide-react";
import "../../../styles/pages/Scope1ActivityListing.css";

const fmt = (val, decimals = 2) =>
  val !== null && val !== undefined && val !== "" && !isNaN(Number(val))
    ? Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : "—";

const Scope1ActivityListing = () => {
  const { reportingYearId } = useReportingYear();
  const location = useLocation();
  const navigate = useNavigate();

  const { title, entityId } = location.state || {};
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!reportingYearId) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get("/api/scope1-activity-list", {
          params: {
            reporting_year_id: reportingYearId,
            scope: "scope1",
            legal_entity_id: entityId,
          },
        });
        if (res.data.success) {
          setApiData(res.data);
        } else {
          setError(res.data.message || "Failed to load Scope 1 data.");
        }
      } catch (err) {
        setError("An error occurred while fetching Scope 1 data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reportingYearId, entityId]);

  if (loading) return <div className="listing-container">Loading...</div>;
  if (error) return <div className="listing-container" style={{ color: "red" }}>{error}</div>;

  const rows = apiData?.data || [];
  const contributions = apiData?.contribution || [];
  const totalValue = apiData?.total_emission ?? 0;

  return (
    <div className="listing-container">
     

      {apiData && (
        <>
          <div className="hero-card">
            <div className="icon-badge">
              <Flame size={20} color="#16a34a" />
            </div>
            <div>
              <h1 className="hero-title">{title || "Scope 1 Emissions Breakdown"}</h1>
              <p className="hero-subtitle">Direct emissions from fuel consumption</p>
              <div className="hero-value">
                {fmt(totalValue, 2)} <span className="hero-unit">tCO₂e</span>
              </div>
            </div>
          </div>

          <div className="table-container">
            <h2 className="section-title">Breakdown by Fuel Type</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Fuel Type</th>
                  <th style={{ textAlign: "right" }}>Quantity</th>
                  <th style={{ textAlign: "left" }}>Unit</th>
                  <th style={{ textAlign: "right" }}>Emission Factor (tCO₂e / unit)</th>
                  <th style={{ textAlign: "right" }}>Emissions (tCO₂e)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ textAlign: "left" }}>{item.activity_type}</td>
                    <td style={{ textAlign: "right" }}>{fmt(item.value_total, 0)}</td>
                    <td style={{ textAlign: "left" }}>{item.unit}</td>
                    <td style={{ textAlign: "right" }}>{item.emission_factor_value}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(item.total_emission, 2)}</td>
                  </tr>
                ))}
                <tr className="row-total">
                  <td colSpan={4} style={{ fontWeight: 600 }}>Total</td>
                  <td style={{ textAlign: "right", fontWeight: 700 }}>
                    {fmt(totalValue, 2)} tCO₂e
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="contribution-container">
            <h2 className="section-title">Contribution by Fuel Type</h2>
            <div style={{ marginTop: "20px" }}>
              {contributions.map((c, idx) => (
                <div key={idx} className="bar-wrapper">
                  <div className="bar-header">
                    <span className="bar-label">{c.activity_type}</span>
                    <span className="bar-meta">
                      {fmt(c.total_emission, 2)} tCO₂e - <strong>{Number(c.percentage).toFixed(1)}%</strong>
                    </span>
                  </div>
                  <div className="bar-track">
                    <div className="bar-fill" style={{ width: `${c.percentage}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <div className="ws-back">
<button onClick={() => navigate(-1)} className="back-link">
        ← Back to Environmental Summary
      </button>
      </div>
    </div>
  );
};

export default Scope1ActivityListing;